import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { Badge, Link, Tooltip, Chip, Divider } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router-dom";
import LoginButton from "./LoginButton";
import LogoutButton from "./LogoutButton";
import ProfileButton from "./ProfileButton";
import GroupIcon from "@mui/icons-material/Group";
import FolderIcon from "@mui/icons-material/Folder";
import MailIcon from "@mui/icons-material/Mail";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { inviteApi, adminApi } from "../utils/api";
import { useFeatureFlags } from "../hooks/useFeatureFlags";

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  badge?: number;
  active: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, path, badge, active, onClick }) => (
  <Tooltip title={label} arrow>
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.1)",
        },
      }}
    >
      {badge !== undefined && badge > 0 ? (
        <Badge badgeContent={badge} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16 } }}>
          {icon}
        </Badge>
      ) : (
        icon
      )}
      <Typography
        variant="body2"
        sx={{
          fontWeight: active ? 600 : 400,
          fontSize: "0.875rem",
          display: { xs: "none", sm: "block" },
        }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

export default function ButtonAppBar() {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingInviteCount, setPendingInviteCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { canAccessGroups, canAccessCollections, canAccessInvites } = useFeatureFlags();

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;
      try {
        const token = await getAccessTokenSilently();
        const [invites, adminCheck] = await Promise.all([
          inviteApi.getMyPending(token).catch(() => []),
          adminApi.checkSuperAdmin(token).catch(() => ({ isSuperAdmin: false })),
        ]);
        setPendingInviteCount(invites.length);
        setIsSuperAdmin(adminCheck.isSuperAdmin);
      } catch (err) {
        console.error("Failed to fetch nav data:", err);
      }
    };
    fetchData();
  }, [isAuthenticated, getAccessTokenSilently]);

  const isActive = (path: string) => location.pathname.startsWith(path);
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #73000a 0%, #990012 100%)" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Logo & Brand */}
          <Box 
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <img
              src="/logo.png"
              alt="ACTT Logo"
              style={{ height: 36, marginRight: 10 }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                letterSpacing: "0.5px",
                display: { xs: "none", md: "block" }
              }}
            >
              ACTT Study
            </Typography>
          </Box>
          
          {/* Right side: Navigation + Profile */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isAuthenticated && (canAccessGroups || canAccessCollections || canAccessInvites || isSuperAdmin) && (
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "rgba(0,0,0,0.15)",
                  borderRadius: 3,
                  px: 1,
                  py: 0.5,
                }}
              >
                {canAccessGroups && (
                  <NavItem
                    icon={<GroupIcon sx={{ fontSize: 20 }} />}
                    label="Groups"
                    path="/groups"
                    active={isActive("/groups")}
                    onClick={() => navigate("/groups")}
                  />
                )}
                {canAccessCollections && (
                  <NavItem
                    icon={<FolderIcon sx={{ fontSize: 20 }} />}
                    label="Collections"
                    path="/collections"
                    active={isActive("/collections")}
                    onClick={() => navigate("/collections")}
                  />
                )}
                {canAccessInvites && (
                  <NavItem
                    icon={<MailIcon sx={{ fontSize: 20 }} />}
                    label="Invites"
                    path="/invites"
                    badge={pendingInviteCount}
                    active={isActive("/invites")}
                    onClick={() => navigate("/invites")}
                  />
                )}
                {isSuperAdmin && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.2)", mx: 0.5 }} />
                    <NavItem
                      icon={<AdminPanelSettingsIcon sx={{ fontSize: 20 }} />}
                      label="Admin"
                      path="/admin"
                      active={isActive("/admin")}
                      onClick={() => navigate("/admin")}
                    />
                  </>
                )}
              </Box>
            )}
            
            {/* Profile/Login */}
            <Box>{isAuthenticated ? <ProfileButton /> : <LoginButton />}</Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
